import { ExportOutlined } from "@ant-design/icons";
import { useTranslation } from "@impero-com/common/utils/i18n";
import {
    BlockPresenceTransition,
    Divider,
    FormLabel,
    renderMarkdown,
    useBelowBreakpoint,
    Button,
} from "@impero-com/ui";
import { Typography } from "antd";
import { cx } from "class-variance-authority";
import { Control, useWatch } from "react-hook-form";

import {
    ControlProgramOverview,
    ControlWithoutFieldsGraphItemDto,
} from "api/generated/model";
import { useControlsQuery } from "org/control/queries/useControlsQuery";
import { formatControlSchedulingType } from "org/control/utils";

import { WebRoute } from "../../../../../routes";
import { TestingControlFormSchema } from "../testingControlSchema";

import * as theme from "./ControlToTestSection.css";
import { TextSkeleton } from "./TextSkeleton";

const { Text } = Typography;

interface ControlToTestSectionProps {
    control: Control<TestingControlFormSchema>;
    disableInTransition: boolean;
}
export function ControlToTestSection({
    control,
    disableInTransition,
}: ControlToTestSectionProps) {
    const { data: controlsData, status } = useControlsQuery();

    const controlToTestId = useWatch({ control, name: "controlId" });
    if (!controlToTestId) {
        return null;
    }
    if (status === "error") {
        return null;
    }

    const controlToTest = controlsData?.content.find(
        (control) => control.resource.id === controlToTestId
    );
    // User may not have access to control, in which case we don't render the section
    if (!controlToTest && status === "success") {
        return null;
    }
    const relatedControlProgram =
        controlsData?.linkedResources.controlPrograms.find(
            (program) =>
                program.resource.id === controlToTest?.resource.controlProgramId
        );

    return (
        <BlockPresenceTransition
            disableInitial={disableInTransition}
            isVisible={!!controlToTestId}
        >
            <ControlToTestSectionContent
                controlProgram={relatedControlProgram?.resource}
                controlResource={controlToTest?.resource}
            />
        </BlockPresenceTransition>
    );
}

interface ControlToTestSectionContentProps {
    controlResource: ControlWithoutFieldsGraphItemDto | undefined;
    controlProgram: ControlProgramOverview | undefined;
}
function ControlToTestSectionContent({
    controlResource,
    controlProgram,
}: ControlToTestSectionContentProps) {
    const { t } = useTranslation();

    const belowXl = useBelowBreakpoint("xl");

    return (
        <div className={cx(theme.root, belowXl && theme.rootMobile)}>
            <div className={theme.controlToTestContent}>
                <FormLabel>
                    {t("TestingControlView_ControlToTest_Selected")}
                </FormLabel>

                {controlResource?.title ? (
                    <Text>{controlResource.title}</Text>
                ) : (
                    <TextSkeleton />
                )}

                <Divider spacing="small" />

                <FormLabel>
                    {t("TestingControlView_ControlToTest_Description")}
                </FormLabel>
                <ControlDescription controlResource={controlResource} />

                <Divider />

                <FormLabel>
                    {t("TestingControlView_ControlToTest_ScheduleType")}
                </FormLabel>
                <ControlScheduling controlResource={controlResource} />

                <Divider spacing="small" />

                <FormLabel>
                    {t("TestingControlView_ControlToTest_ControlProgram")}
                </FormLabel>
                {controlProgram?.name ? (
                    <Text>{controlProgram.name}</Text>
                ) : (
                    <TextSkeleton />
                )}

                <Divider spacing="small" />

                {controlResource?.id ? (
                    <ControlButton controlId={controlResource.id} />
                ) : (
                    <TextSkeleton />
                )}
            </div>
        </div>
    );
}

interface ControlButtonProps {
    controlId: string;
}
function ControlButton({ controlId }: ControlButtonProps) {
    const { t } = useTranslation();
    return (
        <Button variant="link" asChild>
            <a href={`${WebRoute.CONTROLS}/${controlId}`} target="_blank">
                <ExportOutlined className={theme.linkIcon} />
                {t("Select_Control_GoToButton_Text")}
            </a>
        </Button>
    );
}

interface ControlDescriptionProps {
    controlResource: ControlWithoutFieldsGraphItemDto | undefined;
}
function ControlDescription({ controlResource }: ControlDescriptionProps) {
    const { t } = useTranslation();

    if (!controlResource) {
        return <TextSkeleton />;
    }

    if (!controlResource.description) {
        return (
            <Text italic type="secondary">
                {t("TestingControlView_ControlToTest_NoDescription")}
            </Text>
        );
    }

    return <Text>{renderMarkdown(controlResource.description)}</Text>;
}

interface ControlSchedulingProps {
    controlResource: ControlWithoutFieldsGraphItemDto | undefined;
}
function ControlScheduling({ controlResource }: ControlSchedulingProps) {
    const { t } = useTranslation();

    if (!controlResource) {
        return <TextSkeleton />;
    }

    if (!controlResource.scheduling) {
        return (
            <Text italic type="secondary">
                {t("TestingControlView_ControlToTest_NoScheduling")}
            </Text>
        );
    }

    return (
        <Text>
            {formatControlSchedulingType(
                controlResource.scheduling?.rule.type,
                t
            )}
        </Text>
    );
}
